import { supabase } from './supabase';

export async function uploadListingImages(images: File[]) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  // Use authenticated user ID for storage path
  const uploads = images.map(async (image, index) => {
    const fileExt = image.name.split('.').pop();
    const timestamp = new Date().getTime();
    const filePath = `${user.id}/${timestamp}-${index}.${fileExt}`;

    console.log('Uploading image to path:', filePath);
    
    // Use _data to indicate we're ignoring the variable
    const { error } = await supabase.storage
      .from('listing-images')
      .upload(filePath, image, {
        cacheControl: '3600',
        upsert: true // Use upsert to handle duplicates
      });

    if (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
    return filePath;
  });

  return Promise.all(uploads);
}

// Function is now for getting images for the current authenticated user
export async function getListingImages() {
  // Get current authenticated user 
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { urls: [], error: new Error('User not authenticated') };
  }
  
  // Use the authenticated user's ID
  const userId = user.id;
  
  try {
    console.log(`Listing images for user: ${userId}`);
    
    const { data: files, error: listError } = await supabase.storage
      .from('listing-images')
      .list(userId);

    if (listError) {
      console.error('Error listing images:', listError);
      return { urls: [], error: listError };
    }

    console.log(`Found ${files?.length || 0} files in storage for user: ${userId}`);
    
    const urls = files?.map(file => {
      const { data } = supabase.storage
        .from('listing-images')
        .getPublicUrl(`${userId}/${file.name}`);
      return data.publicUrl;
    }) || [];

    return { urls, error: null };
  } catch (error) {
    console.error('Error in getListingImages:', error);
    return { urls: [], error };
  }
}

/**
 * Delete a specific image from storage using its public URL
 * @param imageUrl - The full public URL of the image to delete
 * @returns Object with error field if there's an error
 */
export async function deleteImageFromStorage(imageUrl: string) {
  try {
    // First, check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { error: new Error('User not authenticated') };
    }
    
    // Extract the path from the URL
    // URLs are in format: https://[project-ref].supabase.co/storage/v1/object/public/listing-images/[user-id]/[filename]
    const urlParts = imageUrl.split('/listing-images/');
    if (urlParts.length < 2) {
      console.error('Invalid image URL format:', imageUrl);
      return { error: new Error('Invalid image URL format') };
    }
    
    // Get the path after 'listing-images/'
    const storagePath = urlParts[1];
    console.log('Deleting image with path:', storagePath);
    
    // Remove the file
    const { error: deleteError } = await supabase.storage
      .from('listing-images')
      .remove([storagePath]);
    
    if (deleteError) {
      console.error('Error deleting image from storage:', deleteError);
      return { error: deleteError };
    }
    
    console.log('Successfully deleted image:', imageUrl);
    return { error: null };
  } catch (error) {
    console.error('Error in deleteImageFromStorage:', error);
    return { error };
  }
}

/**
 * Clean up orphaned images for a user - images that exist in storage but aren't referenced in any listings
 * This is a non-destructive function that only cleans up images that are no longer referenced in the database
 */
export async function cleanupOrphanedImages() {
  try {
    // First, check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { error: new Error('User not authenticated') };
    }
    
    // Step 1: Get all images from the user's storage folder
    const { data: files, error: listError } = await supabase.storage
      .from('listing-images')
      .list(user.id);

    if (listError) {
      console.error('Error listing storage files:', listError);
      return { error: listError };
    }

    if (!files || files.length === 0) {
      console.log('No files found in storage for cleanup');
      return { error: null, deletedCount: 0 };
    }
    
    // Generate storage URLs for comparison
    const storageUrls = files.map(file => {
      const { data } = supabase.storage
        .from('listing-images')
        .getPublicUrl(`${user.id}/${file.name}`);
      return data.publicUrl;
    });
    
    // Step 2: Get all the user's listings and their referenced images
    const { data: listings, error: listingsError } = await supabase
      .from('listings')
      .select('image_urls')
      .eq('user_id', user.id);
    
    if (listingsError) {
      console.error('Error fetching user listings:', listingsError);
      return { error: listingsError };
    }
    
    // Flatten all image URLs referenced in listings
    const referencedUrls = listings
      ? listings.flatMap(listing => listing.image_urls || [])
      : [];
    
    console.log(`Found ${storageUrls.length} files in storage and ${referencedUrls.length} referenced in listings`);
    
    // Step 3: Find orphaned images (in storage but not referenced in any listing)
    const orphanedUrls = storageUrls.filter(url => !referencedUrls.includes(url));
    
    if (orphanedUrls.length === 0) {
      console.log('No orphaned images found');
      return { error: null, deletedCount: 0 };
    }
    
    console.log(`Found ${orphanedUrls.length} orphaned images to delete`);
    
    // Step 4: Delete orphaned images
    const deletionResults = await Promise.all(
      orphanedUrls.map(url => deleteImageFromStorage(url))
    );
    
    // Check for any errors
    const deletionErrors = deletionResults
      .filter(result => result.error)
      .map(result => result.error);
    
    if (deletionErrors.length > 0) {
      console.error(`Encountered ${deletionErrors.length} errors during cleanup`);
      return { 
        error: new Error(`${deletionErrors.length} errors during cleanup`), 
        deletedCount: orphanedUrls.length - deletionErrors.length 
      };
    }
    
    console.log(`Successfully cleaned up ${orphanedUrls.length} orphaned images`);
    return { error: null, deletedCount: orphanedUrls.length };
  } catch (error) {
    console.error('Error in cleanupOrphanedImages:', error);
    return { error, deletedCount: 0 };
  }
}

export async function deleteListingImages() {
  // Get authenticated user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { error: new Error('User not authenticated') };
  }
  
  try {
    console.log(`Deleting images for user: ${user.id}`);
    
    // List all files in the user's folder
    const { data: files, error: listError } = await supabase.storage
      .from('listing-images')
      .list(user.id);

    if (listError) {
      console.error('Error listing files to delete:', listError);
      return { error: listError };
    }

    if (files && files.length > 0) {
      console.log(`Found ${files.length} files to delete`);
      const filePaths = files.map(file => `${user.id}/${file.name}`);
      
      const { error: deleteError } = await supabase.storage
        .from('listing-images')
        .remove(filePaths);

      if (deleteError) {
        console.error('Error deleting files:', deleteError);
      } else {
        console.log(`Successfully deleted ${filePaths.length} files`);
      }
      
      return { error: deleteError };
    } else {
      console.log('No files found to delete');
    }

    return { error: null };
  } catch (error) {
    console.error('Error in deleteListingImages:', error);
    return { error };
  }
}